import React from 'react';

import {
  IconBolt,
  IconBoltFilled,
  IconCalculator,
  IconCalculatorFilled,
  IconFlask,
  IconFlaskFilled,
  IconFunction,
  IconFunctionFilled,
} from '@tabler/icons-react';

import { BaseColorKey, PureColorKey, SubjectColorKey } from '../../theme';

import { HexColor } from '@repo/types/domain';
import {
  COURSE_ENROLMENT_STATES as SHARED_COURSE_ENROLMENT_STATES,
  CourseEnrolmentState,
  Grade,
  Location,
  StudentActivityReasonCategory,
  StudentActivityType,
  StudentState,
  Subject,
  UnenrolmentType,
} from '@repo/validation';

// Above a page of options, with headroom for call sites that drop already-selected people.
export const USER_SEARCH_LIMIT = 50;

export const PERMISSION_GROUP_PREFIX = 'CPG';
export const CLASS_PERMISSION_GROUP_PREFIX = 'CL';

export const DEFAULT_LOCATION = Location.Chatswood;

export const DAYS_IN_WEEK = [
  {
    label: 'Undetermined',
    shortLabel: 'N/A',
    value: -1,
  },
  {
    label: 'Monday',
    shortLabel: 'Mon',
    value: 1,
  },
  {
    label: 'Tuesday',
    shortLabel: 'Tue',
    value: 2,
  },
  {
    label: 'Wednesday',
    shortLabel: 'Wed',
    value: 3,
  },
  {
    label: 'Thursday',
    shortLabel: 'Thu',
    value: 4,
  },
  {
    label: 'Friday',
    shortLabel: 'Fri',
    value: 5,
  },
  {
    label: 'Saturday',
    shortLabel: 'Sat',
    value: 6,
  },
  {
    label: 'Sunday',
    shortLabel: 'Sun',
    value: 0,
  },
];

export interface SubjectDetails {
  key: string;
  colorKey: SubjectColorKey;
  label: string;
  value: string;
  icon: React.ReactElement | React.ReactElement[];
  iconFilled?: React.ReactElement | React.ReactElement[];
}

export interface StudentStateDetails {
  key: string;
  colorKey: PureColorKey | BaseColorKey;
  label: string;
  value: string;
}

export const DEFAULT_PROFILE_COLOR: HexColor = '#0c374d';
export const PROFILE_COLOR_OPTIONS: HexColor[] = [
  '#0c374d',
  '#1287a8',
  '#667541',
  '#a06681',
  '#6b3975',
  '#cd594a',
];

export const ADAPTER_DAY_JS_LOCALE = 'en-au';

export const ENROLLABLE_STUDENT_STATES = [
  StudentState.Lead,
  StudentState.Enrolled,
  StudentState.PendingAttendance,
  StudentState.Active,
  StudentState.OnTrial,
];

export const STUDENT_STATES: { [key: string]: StudentStateDetails } = {
  [StudentState.Lead]: {
    key: StudentState.Lead,
    colorKey: 'lightRed',
    value: StudentState.Lead,
    label: 'Lead',
  },
  [StudentState.Enrolled]: {
    key: StudentState.Enrolled,
    colorKey: 'lightOrange',
    value: StudentState.Enrolled,
    label: 'Enrolled',
  },
  [StudentState.PendingAttendance]: {
    key: StudentState.PendingAttendance,
    colorKey: 'lightYellow',
    value: StudentState.PendingAttendance,
    label: 'Pending attendance',
  },
  [StudentState.OnTrial]: {
    key: StudentState.OnTrial,
    colorKey: 'lightYellow',
    value: StudentState.OnTrial,
    label: 'On trial',
  },
  [StudentState.Active]: {
    key: StudentState.Active,
    colorKey: 'lightGreen',
    value: StudentState.Active,
    label: 'Active',
  },
  [StudentState.Graduated]: {
    key: StudentState.Graduated,
    colorKey: 'lightBlue',
    value: StudentState.Graduated,
    label: 'Graduated',
  },
  [StudentState.Archived]: {
    key: StudentState.Archived,
    colorKey: 'lightBlue',
    value: StudentState.Archived,
    label: 'Archived',
  },
};

export const SUBJECTS: { [key: string]: SubjectDetails } = {
  chem: {
    key: Subject.Chemistry,
    value: Subject.Chemistry,
    label: 'Chemistry',
    colorKey: 'chemistry',
    icon: <IconFlask />,
    iconFilled: <IconFlaskFilled />,
  },
  phys: {
    key: Subject.Physics,
    value: Subject.Physics,
    label: 'Physics',
    colorKey: 'physics',
    icon: <IconBolt />,
    iconFilled: <IconBoltFilled />,
  },
  mtx1: {
    key: Subject.MathsExt1,
    value: Subject.MathsExt1,
    label: 'Maths Ext 1',
    colorKey: 'mathsExt1',
    icon: <IconCalculator />,
    iconFilled: <IconCalculatorFilled />,
  },
  mtx2: {
    key: Subject.MathsExt2,
    value: Subject.MathsExt2,
    label: 'Maths Ext 2',
    colorKey: 'mathsExt2',
    icon: <IconFunction />,
    iconFilled: <IconFunctionFilled />,
  },
};

export const GRADES = [
  {
    key: Grade.HSC,
    value: Grade.HSC,
    label: 'HSC',
  },
  {
    key: Grade.Prelim,
    value: Grade.Prelim,
    label: 'Prelim',
  },
];

export const ANALYTICS_QUESTION_TYPES = [
  'calculationAnalytics',
  'extendedResponseAnalytics',
  'graphingAnalytics',
  'multipleChoiceAnalytics',
  'proofAnalytics',
  'shortAnswerAnalytics',
];

export const MISTAKE_TYPES = {
  inaccuracyMarks: 'Incorrect',
  sillyMarks: 'Silly mistakes',
  correctMarks: 'Correct',
};

export const ENROLMENT_TYPE = 'ENROL';

export interface ActivityTypeDetails {
  key: string;
  colorKey: PureColorKey | BaseColorKey;
  label: string;
  value: string;
}

export const STUDENT_ACTIVITY_TYPES: { [key: string]: ActivityTypeDetails } = {
  [StudentActivityType.Enrol]: {
    key: StudentActivityType.Enrol,
    value: StudentActivityType.Enrol,
    label: 'Enrol',
    colorKey: 'lightGreen',
  },
  [StudentActivityType.Quit]: {
    key: StudentActivityType.Quit,
    value: StudentActivityType.Quit,
    label: 'Quit',
    colorKey: 'lightRed',
  },
  [StudentActivityType.Move]: {
    key: StudentActivityType.Move,
    value: StudentActivityType.Move,
    label: 'Move',
    colorKey: 'lightOrange',
  },
};

export const COURSE_ENROLMENT_STATES: {
  [key: string]: {
    key: string;
    colorKey: PureColorKey | BaseColorKey;
    label: string;
    value: string;
  };
} = {
  [CourseEnrolmentState.Active]: {
    ...SHARED_COURSE_ENROLMENT_STATES[CourseEnrolmentState.Active],
    colorKey: 'lightGreen',
  },
  [CourseEnrolmentState.OnTrial]: {
    ...SHARED_COURSE_ENROLMENT_STATES[CourseEnrolmentState.OnTrial],
    colorKey: 'lightYellow',
  },
  [CourseEnrolmentState.Pending]: {
    ...SHARED_COURSE_ENROLMENT_STATES[CourseEnrolmentState.Pending],
    colorKey: 'lightBlue',
  },
  [CourseEnrolmentState.Unenrolled]: {
    ...SHARED_COURSE_ENROLMENT_STATES[CourseEnrolmentState.Unenrolled],
    colorKey: 'gray',
  },
};

export const UNENROLMENT_TYPES = [
  {
    key: UnenrolmentType.Quit,
    value: UnenrolmentType.Quit,
    label: 'Quit',
  },
  {
    key: UnenrolmentType.Move,
    value: UnenrolmentType.Move,
    label: 'Move',
  },
];

const UNENROLMENT_SHARED_REASONS = [
  {
    key: StudentActivityReasonCategory.TeacherDissatisfaction,
    value: StudentActivityReasonCategory.TeacherDissatisfaction,
    label: 'Teacher dissatisfaction',
  },
  {
    key: StudentActivityReasonCategory.MismatchingSchedule,
    value: StudentActivityReasonCategory.MismatchingSchedule,
    label: 'Mismatching schedule',
  },
  {
    key: StudentActivityReasonCategory.ScheduleConflict,
    value: StudentActivityReasonCategory.ScheduleConflict,
    label: 'Schedule conflict',
  },
  {
    key: StudentActivityReasonCategory.UnaffordableFees,
    value: StudentActivityReasonCategory.UnaffordableFees,
    label: 'Unaffordable fees',
  },
  {
    key: StudentActivityReasonCategory.AlreadyStudied,
    value: StudentActivityReasonCategory.AlreadyStudied,
    label: 'Already studied / enrolled elsewhere',
  },
  {
    key: StudentActivityReasonCategory.GeographicalInconvenience,
    value: StudentActivityReasonCategory.GeographicalInconvenience,
    label: 'Geographical inconvenience',
  },
  {
    key: StudentActivityReasonCategory.Accident,
    value: StudentActivityReasonCategory.Accident,
    label: 'Accidental enrolment / testing',
  },
  {
    key: StudentActivityReasonCategory.Full,
    value: StudentActivityReasonCategory.Full,
    label: 'Class is full',
  },
  {
    key: StudentActivityReasonCategory.ClassCompletion,
    value: StudentActivityReasonCategory.ClassCompletion,
    label: 'Class is finished / cancelled',
  },
  {
    key: StudentActivityReasonCategory.Unknown,
    value: StudentActivityReasonCategory.Unknown,
    label: 'Unknown',
  },
];

export const UNENROLMENT_QUIT_REASONS = [
  ...UNENROLMENT_SHARED_REASONS,
  {
    key: StudentActivityReasonCategory.AdminDissatisfaction,
    value: StudentActivityReasonCategory.AdminDissatisfaction,
    label: 'Admin dissatisfaction',
  },
  {
    key: StudentActivityReasonCategory.ServiceDissatisfaction,
    value: StudentActivityReasonCategory.ServiceDissatisfaction,
    label: 'Service dissatisfaction',
  },
  {
    key: StudentActivityReasonCategory.NeverAttended,
    value: StudentActivityReasonCategory.NeverAttended,
    label: 'Never attended',
  },
  {
    key: StudentActivityReasonCategory.Graduation,
    value: StudentActivityReasonCategory.Graduation,
    label: 'Graduation',
  },
  {
    key: StudentActivityReasonCategory.DroppedCourse,
    value: StudentActivityReasonCategory.DroppedCourse,
    label: 'Dropped course',
  },
  {
    key: StudentActivityReasonCategory.TooEasy,
    value: StudentActivityReasonCategory.TooEasy,
    label: 'Too easy / slow',
  },
  {
    key: StudentActivityReasonCategory.TooHard,
    value: StudentActivityReasonCategory.TooHard,
    label: 'Too hard / fast',
  },
  {
    key: StudentActivityReasonCategory.NoImprovement,
    value: StudentActivityReasonCategory.NoImprovement,
    label: 'No improvement',
  },
];

export const UNENROLMENT_MOVE_REASONS = [
  ...UNENROLMENT_SHARED_REASONS,
  {
    key: StudentActivityReasonCategory.FriendPreference,
    value: StudentActivityReasonCategory.FriendPreference,
    label: 'Be with school / friends',
  },
];

export const DEFAULT_UNENROLMENT_REASON = {
  key: StudentActivityReasonCategory.Unknown,
  value: StudentActivityReasonCategory.Unknown,
  label: 'Unknown',
};

const ALL_REASON_OPTIONS = [...UNENROLMENT_QUIT_REASONS, ...UNENROLMENT_MOVE_REASONS];

export const getReasonCategoryLabel = (category: string): string => {
  const match = ALL_REASON_OPTIONS.find(option => option.value === category);
  return match?.label ?? category;
};

export const IndexDBDatabase = {
  Pdfs: {
    name: 'Pdfs',
    version: 1,
    defaultObjectStore: 'default',
    objectStores: {
      default: 'default',
      examAttempts: 'examAttempts',
    },
  },
};

export enum CacheKey {
  UserId = 'userId',
  UserGroups = 'userGroups',
  ThemeMode = 'themeMode',
  ExamContentsSidebarSwitch = 'examContentsSidebar',
  QuestionTreeSidebarSwitch = 'questionTreeSidebar',
  ExamViewerPanelSize = 'examViewerPanelSize',
  SidePanelWidth = 'sidePanelWidth',
  RollSummaryBoardSetup = 'rollSummaryBoardSetup',
  /** Session storage: set once this tab has reloaded over a failed chunk. */
  ChunkReloaded = 'chunkReloaded',
  /** Session storage: when this tab last reloaded because the API turned its build away. */
  OutdatedClientReloadedAt = 'outdatedClientReloadedAt',
  /** Session storage: when this tab last reloaded onto a newly deployed build. */
  NewBuildReloadedAt = 'newBuildReloadedAt',
  /** Session storage: where this tab is in the student walkthrough. */
  OnboardingStep = 'onboardingStep',
  /** Session storage: set once this tab has recorded the walkthrough finished. */
  OnboardingFinishRecorded = 'onboardingFinishRecorded',
  /** Session storage: set once the student has left the walkthrough in this tab. */
  OnboardingClosed = 'onboardingClosed',
  /** Session storage: where this tab is in a manager's preview of the walkthrough. */
  OnboardingPreviewStep = 'onboardingPreviewStep',
}

export enum FileUrlMode {
  Download = 'download',
  Upload = 'upload',
}

export enum LegacyExamFileType {
  Marked = 'marked',
  Unmarked = 'unmarked',
}

export enum LegacyExamFileFetchPurpose {
  Display = 'display',
  Download = 'download',
}
